import { v4 as uuidv4 } from 'uuid';

// Frontend-initiated end-to-end correlation header (RUN-135). Tags each individual
// request so it can be traced through Datadog from browser → API Gateway → Lambda.
export const INITIATED_CORRELATION_HEADER = 'x-correlation-id';

export const generateCorrelationId = (): string => uuidv4();

const unsupported = new Set<string>();

const normaliseBase = (endpoint: string): string => endpoint.replace(/\/+$/, '');

function correlationHeader(endpoint: string): Record<string, string> {
    if (unsupported.has(normaliseBase(endpoint))) return {};
    return { [INITIATED_CORRELATION_HEADER]: generateCorrelationId() };
}

// A CORS-blocked request never reaches the server. Detection looks for the shapes
// the browser and axios actually produce, not just "no .response":
//   - native fetch: TypeError ("Failed to fetch")
//   - axios:        code === 'ERR_NETWORK' or message === 'Network Error'
// HTTP errors from the Lambda (4xx/5xx) carry err.response.status, so they're easy
// to exclude. Anything that doesn't match propagates without retry — keeps
// unrelated transient errors from triggering the unsupported-marking path.
function isLikelyCorsBlock(error: unknown): boolean {
    if (typeof error !== 'object' || error === null) return false;
    const e = error as { response?: unknown; code?: string; message?: string };
    if (e.response) return false;
    if (error instanceof TypeError) return true;
    if (e.code === 'ERR_NETWORK') return true;
    if (e.message === 'Network Error') return true;
    return false;
}

function markUnsupported(endpoint: string): void {
    const base = normaliseBase(endpoint);
    if (unsupported.has(base)) return;
    unsupported.add(base);
    console.error(
        `[CorrelationId] BACKEND MISSING CORS CONFIG: ${base} did not allow ${INITIATED_CORRELATION_HEADER} through CORS preflight. The first call was retried without the header and succeeded; tracing is now disabled for this gateway for the rest of the session. Ship RUN-135's serverless.yml change on that service. (If the gateway is otherwise healthy, investigate transient network failure.)`
    );
}

// Helper for callers that pass init objects to Amplify-style APIs: only attach a
// `headers` field when there's actually a header to set, so callers that compare
// init objects exactly (older test mocks) aren't broken by an empty headers prop.
export const withInitHeaders = <T extends object>(init: T, extraHeaders: Record<string, string>): T =>
    Object.keys(extraHeaders).length ? { ...init, headers: extraHeaders } : init;

// Wraps a request so that:
// - the first call carries x-correlation-id (optimistic)
// - if the call fails in a way consistent with a CORS preflight block AND the
//   caller marked the request safe to retry, retry once without x-correlation-id
//   (and if THAT succeeds, mark the gateway unsupported + emit a loud console.error)
// - if the retry also fails, propagate without marking (real outage, not a CORS
//   issue) so transient failures don't permanently disable tracing
// - subsequent calls to a marked gateway skip x-correlation-id from the start
//
// `canRetry` defaults to false so that POST / PUT / PATCH / DELETE — anything
// non-idempotent — propagates the failure without a duplicate write. Pass
// `{ canRetry: true }` for GET / HEAD. In practice the first GET to a misconfigured
// gateway marks it unsupported via the retry-success path, so subsequent write
// calls in the same session skip the header automatically.
export async function withCorrelationRetry<R>(
    endpoint: string | undefined,
    makeCall: (extraHeaders: Record<string, string>) => Promise<R>,
    options: { canRetry?: boolean } = {}
): Promise<R> {
    if (!endpoint) return makeCall({});

    const header = correlationHeader(endpoint);
    try {
        return await makeCall(header);
    } catch (error) {
        if (!options.canRetry || !header[INITIATED_CORRELATION_HEADER] || !isLikelyCorsBlock(error)) {
            throw error;
        }
        const retryResult = await makeCall({});
        markUnsupported(endpoint);
        return retryResult;
    }
}

export function __resetCorrelationIdSupportForTests(): void {
    unsupported.clear();
}
